// Imports
import Vue from "vue";
import Router from "vue-router";
import { trailingSlash } from "@/util/helpers";
import { layout, route } from "@/util/routes";
import {
  hasFullMenuAccess,
  normalizePermissionPath,
  requiresExplicitPermission
} from "@/util/access";

Vue.use(Router);

const router = new Router({
  mode: "history",
  base: process.env.BASE_URL,
  scrollBehavior: (to, from, savedPosition) => {
    if (to.hash) return { selector: to.hash };
    if (savedPosition) return savedPosition;

    return { x: 0, y: 0 };
  },
  routes: [
    {
      path: "/login",
      name: "login",
      component: () => import(/* webpackChunkName: "login" */ "@/views/Login")
    },
    layout("Default", [
      route("Dashboard"),
      route("Dashboard Owner", { default: "DashboardOwner" }, "dashboard-owner"),

      // Penerimaan Barang (TTB)
      route("TTB", null, "ttb/input-edit-ttb"),
      route("TTB Tidak ada Ukuran", null, "ttb/ttb-tidak-ada-ukuran"),
      route(
        "TTB Belum Masuk Container",
        null,
        "ttb/ttb-belum-belum-masuk-container"
      ),

      // Container
      route(
        "Daftar Container Belum Berangkat",
        null,
        "container/daftar-container-belum-berangkat"
      ),
      route("Container", null, "container/input-edit-container"),
      route("Packing List", null, "container/packing-list"),
      route(
        "Cetak KS By Kode Container",
        { default: "CetakKSByKC" },
        "container/cetak-ks-by-kc"
      ),

      // Vessel
      route("Vessel", null, "vessel/input-data-vessel"),
      route("Vessel Data", null, "vessel/data-vessel"),
      route("Masterlis", null, "vessel/masterlis"),

      // Bill of Lading
      route("InputNoBL", null, "bill-of-lading/input-no-bl"),
      route("CetakDataBL", null, "bill-of-lading/cetak-data-bl"),

      // Penyerahan Cargo
      route("TTPB", null, "penyerahan-cargo/ttpb"),
      route("TTPB Kembali", null, "penyerahan-cargo/ttpb-kembali"),
      route("TTPB Belum Kembali", null, "penyerahan-cargo/ttpb-belum-kembali"),
      route(
        "TTPBKembaliPerhari",
        null,
        "penyerahan-cargo/ttpb-kembali-perhari"
      ),
      route(
        "BeritaAcaraKembali",
        null,
        "penyerahan-cargo/berita-acara-kembali"
      ),
      route(
        "BeritaAcaraDikirim",
        null,
        "penyerahan-cargo/berita-acara-dikirim"
      ),

      // Invoice
      route("Invoice", null, "invoice/input-edit-invoice"),
      route("List Admin", null, "invoice/list-admin"),
      route("Print Nota ND", null, "invoice/print-nota-nd"),
      route("Print Nota P", null, "invoice/print-nota-p"),
      route("Print Nota By Nomor", null, "invoice/print-nota-by-nomor"),
      route("Rekap Nota Per Kapal", null, "invoice/rekap-nota-per-kapal"),
      route("TTB Belum Ada Nota", null, "invoice/ttb-belum-ada-nota"),
      route("Penyebaran Nota", null, "invoice/penyebaran-nota"),
      route("Pelunasan Nota", null, "invoice/pelunasan-nota"),
      route(
        "Laporan Penagihan Harian",
        null,
        "invoice/laporan-penagihan-harian"
      ),
      route(
        "Laporan Nota Lunas Harian",
        null,
        "invoice/laporan-nota-lunas-harian"
      ),
      route(
        "Laporan Nota Belum Lunas",
        null,
        "invoice/laporan-nota-belum-lunas"
      ),
      route(
        "Laporan Tagihan Per Kapal",
        { default: "LaporanTagihanPerKapal" },
        "invoice/laporan-tagihan-per-kapal"
      ),
      route(
        "Laporan Tagihan Per Customer",
        { default: "LaporanTagihanPerCustomer" },
        "invoice/laporan-tagihan-per-customer"
      ),
      route(
        "Laporan Marketing",
        { default: "LaporanMarketing" },
        "invoice/laporan-marketing"
      ),
      route(
        "Laporan Marketing Alias",
        { default: "LaporanMarketing" },
        "laporan-marketing"
      ),
      route(
        "Laporan Marketing Group Alias",
        { default: "LaporanMarketing" },
        "marketing/laporan-marketing"
      ),
      route(
        "Laporan Belum Lunas Per Customer",
        { default: "LaporanBelumLunasPerCustomer" },
        "invoice/laporan-belum-lunas-per-customer"
      ),
      route(
        "Laporan Tagihan",
        { default: "LaporanOmsetTagihan" },
        "invoice/laporan-omset-tagihan"
      ),
      route(
        "Laporan Pemotongan",
        { default: "LaporanPemotongan" },
        "invoice/laporan-pemotongan"
      ),
      route(
        "Pemotongan Rekap",
        { default: "PemotonganRekap" },
        "invoice/pemotongan-rekap"
      ),
      route(
        "Laporan Claim Tanpa No Laporan",
        null,
        "invoice/laporan-claim-tanpa-no-lk"
      ),

      // Laporan Container
      route(
        "Laporan Pisah Container",
        { default: "LaporanPisahContainer" },
        "container/laporan-pisah-container"
      ),

      // Laporan TTB Belum Invoice
      route(
        "TTB Belum Invoice",
        { default: "TTBBelumInvoice" },
        "invoice/ttb-belum-invoice"
      ),

      // Tagihan External
      route("InputTagihanExternal", null, "tagihan-external/input-tagihan"),
      route(
        "PelunasanTagihanExternal",
        null,
        "tagihan-external/pelunasan-tagihan"
      ),
      route(
        "TagihanExtBelumLunas",
        null,
        "tagihan-external/tagihan-belum-lunas"
      ),
      route(
        "Laporan Tagihan External",
        { default: "LaporanTagihanExternal" },
        "tagihan-external/laporan"
      ),

      // Declaring dan Kas Eksternal
      route("Declaring", null, "declaring/spk"),
      route("PelunasanDeclaring", null, "declaring/pelunasan"),
      route("KasExternal", null, "kas-eksternal/transaksi"),
      route("LaporanDeclaring", null, "declaring/laporan"),
      route(
        "LaporanDeclaringBelumLunas",
        null,
        "declaring/laporan-belum-lunas"
      ),
      route(
        "LaporanKeuanganKeseluruhan",
        null,
        "laporan/pendapatan-pengeluaran-keseluruhan"
      ),

      // Laporan Kejadian
      route(
        "Input Laporan Kejadian",
        null,
        "laporan-kejadian/input-edit-laporan"
      ),
      route("Laporan Kejadian", null, "laporan-kejadian/lihat-laporan"),
      route("Remarks Management", null, "laporan-kejadian/remarks-management"),
      route(
        "Laporan Sudah Remarks",
        null,
        "laporan-kejadian/laporan-sudah-remarks"
      ),

      // Kasir
      route("Biaya Buruh", null, "kasir/biaya-buruh"),
      route("Biaya Truk", null, "kasir/biaya-truk"),
      route("PengeluaranKas", null, "kasir/pengeluaran-kas"),
      route("Laporan Biaya Buruh Harian", null, "kasir/laporan-biaya-buruh"),
      route("Laporan Biaya Truk Harian", null, "kasir/laporan-biaya-truk"),
      route("Laporan Biaya Lain Harian", null, "kasir/laporan-biaya-lain"),
      route(
        "Laporan Pengeluaran Keseluruhan",
        null,
        "kasir/laporan-pengeluaran-keseluruhan"
      ),
      route("Major Laporan Kasir", null, "kasir/major-lap-harian-kasir"),
      route(
        "Laporan Pengeluaran Kas Tanggal",
        null,
        "kasir/laporan-pengeluaran-kas-tanggal"
      ),
      route(
        "Laporan Pengeluaran Kas Pos",
        null,
        "kasir/laporan-pengeluaran-kas-pos"
      ),

      // Master Data
      route("Surat Jalan", null, "kasir/surat-jalan"),
      route("Kategori Pengeluaran", null, "master-data/kategori-pengeluaran"),
      route("Kategori Laporan Kejadian", null, "master-data/kategori-laporan"),
      route("KategoriTagihan", null, "master-data/kategori-tagihan"),
      route("MasterCustomer", null, "master-data/customer"),
      route("Master Penagih", { default: "MasterPenagih" }, "master-data/penagih"),
      route("Saldo Awal", { default: "SaldoAwal" }, "master-data/saldo-awal"),

      // Giro
      route("Edit Status Giro", null, "giro/edit-status-giro"),
      route("Laporan Giro Belum Cair", null, "giro/laporan-giro-belum-cair"),
      route("Laporan Giro Sudah Cair", null, "giro/laporan-giro-sudah-cair"),

      // Admin
      route("Data Karyawan", null, "admin/kelola-karyawan"),
      route("Hapus TTB", null, "admin/hapus-ttb"),
      route("Hapus Container", null, "admin/hapus-container"),
      route("Hapus Nota", null, "admin/hapus-nota"),
      route("Hapus Biaya Buruh", null, "admin/hapus-biaya-buruh"),
      route("Hapus Biaya Truk", null, "admin/hapus-biaya-truk"),
      route("Hapus Biaya Lain", null, "admin/hapus-biaya-lain"),
      route("Setting Variable", null, "admin/setting-variable"),

      route("Error", null, "*")
    ])
  ]
});

function userHasFullMenuAccess() {
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  return hasFullMenuAccess(user);
}

router.beforeEach((to, from, next) => {
  if (!to.path.endsWith("/")) {
    next(trailingSlash(to.path));
    return;
  }
  if (to.matched.some(record => record.meta.auth)) {
    if (!localStorage.getItem("token")) {
      next("/login");
      return;
    }
    if (userHasFullMenuAccess()) {
      next();
      return;
    }
    if (!hasPermission(to.path)) {
      next("/");
      return;
    }
  }
  next();
});

function hasPermission(path) {
  if (path === "/" || path === "/login") return true;
  const user = JSON.parse(localStorage.getItem("user") || "{}");
  let perms = user.permissions;
  if (typeof perms === "string") {
    try {
      perms = JSON.parse(perms);
    } catch (e) {
      perms = null;
    }
  }
  const clean = normalizePermissionPath(path);
  if (!Array.isArray(perms) || perms.length === 0) {
    return !requiresExplicitPermission(clean);
  }
  return perms.some(p => normalizePermissionPath(p) === clean);
}

export default router;
